import * as workersApi from '../api/workers.js';

const resourceTypes = {
  worker: 'WORKER', kv_namespace: 'KV_NAMESPACE', d1_database: 'D1_DATABASE',
  r2_bucket: 'R2_BUCKET', durable_object: 'DURABLE_OBJECT', queue: 'QUEUE', workflow: 'WORKFLOW',
};
const amount = value => value != null && value !== '' && Number.isFinite(Number(value)) ? `$${value}` : '—';

// No writes before the user confirms the exact environment and server quote.
// A changed quote fails the operation; never silently accept replacement prices.
export async function confirmWorkerFunding(workerId, environment, type, {
  api = workersApi, confirm = message => window.confirm(message),
} = {}) {
  const retention = await api.getWorkerRetention(workerId, environment);
  if (retention?.enabled === false) return {};
  if (retention?.enabled !== true) throw new Error('Retention policy unavailable. Refresh and retry.');
  const quote = await api.getWorkerRetentionQuote(workerId, environment, resourceTypes[type]);
  if (quote?.policyVersion !== 'retention-v3' || !quote.priceVersion || quote.freezeUsd == null)
    throw new Error('Retention quote unavailable or unsupported. Refresh and retry.');
  const policy = retention.lifecycle?.retentionPolicyVersion;
  if (policy && policy !== quote.policyVersion)
    throw new Error('This environment needs an explicit retention policy migration through the CLI.');
  let running = null;
  if (type !== 'worker') {
    let summary;
    try { summary = await api.getWorkerRunningReserve(workerId, environment); }
    catch (error) { if (error.status !== 404) throw error; }
    if (summary?.reserve) {
      if (summary.reserve.state !== 'OPEN') throw new Error('Running funds are closing. Resource creation is unavailable.');
      running = await api.getWorkerRunningReserveQuote(workerId, environment);
      if (!running?.quote?.quoteHash || running.additionalFreezeUsd == null)
        throw new Error('Running funds quote unavailable. Refresh and retry.');
    }
  }
  const message = [
    `Confirm ${environment} funding · ${resourceTypes[type]}`,
    `Retention freeze target: ${amount(quote.freezeUsd)} (existing funds are not frozen twice).`,
    `Available account balance: ${amount(quote.availableBalanceUsd)}. Price version: ${quote.priceVersion}.`,
    ...(running ? [`Additional running funds to freeze: ${amount(running.additionalFreezeUsd)}. Running funds are separate from retention funds.`] : []),
    'Freezing transfers available funds; it is not consumption. Admission is checked atomically by the server.',
    'Resources are automatically deleted when this environment’s retention funds reach the cleanup threshold. The estimate is not a guaranteed retention period.',
    'Deposits do not replenish retention funds or resume service. Recovery must be requested manually. Unused funds are released after confirmed cleanup and settlement.',
    ...(!policy ? ['This also accepts the retention policy for this environment. A verified email is required.'] : []),
    'Continue?',
  ].join('\n\n');
  if (!confirm(message)) return null;
  if (!policy) await api.acceptWorkerRetention(workerId, environment, quote);
  return { retentionPriceVersion: quote.priceVersion, ...(running ? { runningReserveQuoteHash: running.quote.quoteHash } : {}) };
}

export const workerOperationError = error => {
  const code = error?.data?.code;
  if (code === 'verified_email_required') return 'Verify your email in account settings before accepting the retention policy.';
  if (code?.includes('quote')) return `${code}: the funding quote changed or needs confirmation. Review the new quote and retry.`;
  return code ? `${code}: ${error.message}` : error.message || 'Worker operation failed';
};
